import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ClockSolidIcon16pxSvg from "../assets/images/icons/ClockSolidIcon16pxSvg";
import { StyledTextFieldTimePicker } from "./StyledComponents";

const CustomTimePicker = (props) => {
  const { t } = useTranslation();

  const {
    value,
    onTimeChange,
    hoursLabel,
    name,
    maxHours = 23,
    className,
  } = props;

  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [inputValue, setInputValue] = useState("00:00:00");

  /* ================= Utils ================= */
  const formatTime = (h, m, s) =>
    `${String(h).padStart(2, "0")}:${String(m).padStart(
      2,
      "0"
    )}:${String(s).padStart(2, "0")}`;

  const isValidTime = (val) => {
    const match = val.match(/^(\d{2}):(\d{2}):(\d{2})$/);
    if (!match) return false;

    const h = Number(match[1]);
    const m = Number(match[2]);
    const s = Number(match[3]);

    return h <= maxHours && m <= 59 && s <= 59;
  };

  /* ================= Sync from outside ================= */
  useEffect(() => {
    const sanitizedValue = value || "00:00:00";
    setInputValue(sanitizedValue);

    const [h = 0, m = 0, s = 0] = sanitizedValue.split(":").map(Number);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
  }, [value]);

  /* ================= Emit ================= */
  const emitTime = (h, m, s) => {
    const formatted = formatTime(h, m, s);
    setInputValue(formatted);
    onTimeChange?.(formatted);
  };

  /* ================= Input ngoài ================= */
  const handleInputChange = (e) => {
    let val = e.target.value.replace(/[^\d:]/g, "");
    if (val.length > 8) return;

    setInputValue(val);

    if (!isValidTime(val)) return;

    const [h, m, s] = val.split(":").map(Number);
    setHours(h);
    setMinutes(m);
    setSeconds(s);
    onTimeChange?.(val);
  };

  const handleInputBlur = () => {
    if (!isValidTime(inputValue)) {
      emitTime(hours, minutes, seconds);
    }
  };

  /* ================= Dialog handlers ================= */
  const handleHoursChange = (e) => {
    let h = Number(e.target.value);

    if (isNaN(h) || h < 0) h = 0;
    if (h > maxHours) h = maxHours;

    setHours(h);
    emitTime(h, minutes, seconds);
  };

  const handleMinutesChange = (e) => {
    let m = Number(e.target.value);

    if (isNaN(m) || m < 0) m = 0;
    if (m > 59) m = 59;

    setMinutes(m);
    emitTime(hours, m, seconds);
  };

  const handleSecondsChange = (e) => {
    let s = Number(e.target.value);

    if (isNaN(s) || s < 0) s = 0;
    if (s > 59) s = 59;

    setSeconds(s);
    emitTime(hours, minutes, s);
  };

  /* ================= Render ================= */
  return (
    <div>
      {/* ===== Input ngoài ===== */}
      <StyledTextFieldTimePicker
        name={name}
        className={className}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        placeholder="HH:mm:ss"
        variant="outlined"
        size="small"
        inputProps={{
          maxLength: 8,
          inputMode: "numeric",
          pattern: "[0-9]{2}:[0-9]{2}:[0-9]{2}",
        }}
        InputProps={{
          sx: {
            color: "#171717 !important",
            height: "40px",
          },
          endAdornment: (
            <InputAdornment position="end">
              <div className="flex items-center">
                {hoursLabel && (
                  <div className="px-3 py-3 border-l">
                    <p className="text-[#6B7280]">{t("common.hours")}</p>
                  </div>
                )}
                <IconButton
                  onClick={() => setOpen(true)}
                  disableRipple
                  sx={{ height: "40px" }}
                >
                  <ClockSolidIcon16pxSvg />
                </IconButton>
              </div>
            </InputAdornment>
          ),
        }}
      />

      {/* ===== Dialog ===== */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>{t("common.select-time")}</DialogTitle>
        <DialogContent>
          <div className="flex space-x-2 items-center pt-2">
            <TextField
              className="w-28"
              label={t("common.hours")}
              type="number"
              value={String(hours).padStart(2, "0")}
              onChange={handleHoursChange}
              min={0}
              max={23}
            />

            <p>:</p>

            <TextField
              className="w-28"
              label={t("common.minutes")}
              type="number"
              value={String(minutes).padStart(2, "0")}
              onChange={handleMinutesChange}
              min={0}
              max={59}
            />

            <p>:</p>

            <TextField
              className="w-28"
              label={t("common.seconds")}
              type="number"
              value={String(seconds).padStart(2, "0")}
              onChange={handleSecondsChange}
              min={0}
              max={59}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomTimePicker;
